import {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Canceler } from "axios";
import {
    useIsMobile,
    useMessageRedirect,
    useSocket,
    useTrans,
} from "../../../hooks";
import { useAuthData } from "../../../contexts";
import {
    Text,
    Box,
    Flex,
    HStack,
    VStack,
    Button,
    Textarea,
    Hide,
    useToast,
    Spinner,
    Badge,
    Divider,
    AbsoluteCenter,
    Center,
    Progress,
    useDisclosure,
} from "@chakra-ui/react";
import { AppSearch } from "../../../containers";
import {
    AppIcon,
    AppMessage,
    AppMessageThread,
    AppEmojiPicker,
} from "../../../components";
import {
    MessageThread as Entity,
    MessageThreadApi as EntityApi,
    InternshipSchedule,
    Message,
    MessageApi,
    MessageThreadInfo,
    Upload,
    UploadApi,
    User,
    UserApi,
} from "../../../apis";
import { UnprocessableErrorResponse } from "../../../services/ApiPlatform/models";
import { APPC, LSC, UTIL } from "../../../config";
import { socket, SOCKET_EVENT } from "../../../services/Socket";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
    NewThreadMessagesAtom,
    SelectedMessageThreadAtom,
} from "../../../atoms";

export const MessagesPage = () => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const { user, grant } = useAuthData();
    const {
        messageThreadJoin,
        messageThreadLeave,
        messageSend,
        userNotificationSend,
    } = useSocket();
    const { redirectMessageClearActiveTab } = useMessageRedirect();
    const emojiState = useDisclosure();
    const isMobile = useIsMobile();

    // state & const
    const dayjs = UTIL.getDayJs();
    const [search, setSearch] = useState("");
    const [activeTab, setActiveTab] = useState<string | undefined>();

    const [activeMessageThread, setActiveMessageThread] = useState<Entity>();
    const [activeMessageThreadUser, setActiveMessageThreadUser] =
        useState<User>();
    const [loadingMessageThread, setLoadingMessageThread] = useState(true);
    const [messageThreads, setMessageThreads] = useState<Entity[]>([]);
    const [page, setPage] = useState(1);
    const [pagesCount, setPagesCount] = useState(0);

    const [loadingMessage, setLoadingMessage] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [messagePage, setMessagePage] = useState(1);
    const [messagePagesCount, setMessagePagesCount] = useState(0);
    const [loadingSendMessage, setLoadingSendMessage] = useState(false);
    const [messageText, setMessageText] = useState("");
    const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
    const messageThreadListRef = useRef<HTMLDivElement | null>(null);

    const [newThreadMessages, setNewThreadMessages] = useRecoilState(
        NewThreadMessagesAtom
    );
    const setSelectedMessageThread = useSetRecoilState(
        SelectedMessageThreadAtom
    );
    const [loadingAttatch, setLoadingAttatch] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedUploadedFile, setSelectedUploadedFile] = useState<Upload>();
    const [backendError, setBackendError] = useState("");
    const [uploadProgress, setUploadProgress] = useState<number>(0);

    const cancelTokenSourcesRef = useRef<Canceler[]>([]);
    const loggedInUserId = user?.id || 0;

    const fileTypeInfo =
        APPC.BE.Upload.FILETYPEINFO[
            `FILETYPEINFO_${APPC.BE.Upload.FILETYPE.FILETYPE_MESSAGE_FILE}`
        ];

    const groupMessagesByDate = (msgs: Message[]) => {
        return msgs.reduce((groups, m) => {
            const date = dayjs(m.createdAt).format(APPC.APP_DATE_FORMAT);
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(m);
            return groups;
        }, {});
    };

    const groupedMessages = useMemo(
        () => groupMessagesByDate(messages),
        [messages]
    );

    //handler
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (
                file &&
                fileTypeInfo.maxSize &&
                file.size > fileTypeInfo.maxSize * 1024 * 1024
            ) {
                toast({
                    title: t("cmn:val.fileSize.notValid", {
                        size: fileTypeInfo.maxSize,
                    }),
                    status: "error",
                });
            } else {
                setSelectedFile(file);
                submitAttatchHandler(file);
            }
        }
    };

    const handleCancelAttatch = () => {
        cancelTokenSourcesRef.current.forEach((cancelFn) => {
            if (cancelFn) {
                cancelFn();
            }
        });
        cancelTokenSourcesRef.current = [];
        setSelectedFile(null);
        setSelectedUploadedFile(undefined);
        setUploadProgress(0);
        setLoadingAttatch(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const submitAttatchHandler = (file: File) => {
        setLoadingAttatch(true);
        const uploadFormData = new FormData();
        uploadFormData.set(
            "fileType",
            APPC.BE.Upload.FILETYPE.FILETYPE_MESSAGE_FILE
        );
        uploadFormData.set("file", file);

        UploadApi.upload<Upload, FormData>(
            uploadFormData,
            (c) => {
                cancelTokenSourcesRef.current.push(c);
            },
            (p) => setUploadProgress(p)
        )
            .then(({ response, error, errorMessage }) => {
                if (error instanceof UnprocessableErrorResponse) {
                    const { violations } = error;
                    Object.keys(violations).map((key) => {
                        setBackendError(violations[key]);
                    });
                } else if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setSelectedUploadedFile(response);
                }
            })
            .finally(() => {
                setUploadProgress(0);
                setLoadingAttatch(false);
            });
    };

    const submitMessageHandler = async () => {
        const msg = messageText.trim();
        if (msg.length < 1 && !selectedUploadedFile) {
            return;
        }
        const payload = {
            message: msg,
            user: UserApi.toResourceIRI(loggedInUserId),
            status: APPC.BE.Message.STATUS.STATUS_NEW,
        };

        const isThreadExist =
            activeMessageThread?.id && activeMessageThread?.id > 0;

        if (isThreadExist) {
            payload["messageThread"] = EntityApi.toResourceIRI(
                activeMessageThread.id
            );
        } else {
            const newThread = activeMessageThread as MessageThreadInfo;
            payload["messageThreadInfo"] = new MessageThreadInfo({
                messageThreadType: newThread?.messageThreadType,
                members: newThread?.members,
                school: newThread?.school || 0,
                company: newThread?.company || 0,
            });
            if (
                newThread?.messageThreadType ===
                APPC.BE.MessageThread.TYPE.TYPE_INTERNSHIP
            ) {
                payload["messageThreadInfo"].internshipSchedule = (
                    (newThread as Entity)
                        ?.internshipSchedule as InternshipSchedule
                )?.id;
            }
        }

        if (selectedUploadedFile) {
            payload["attachmentName"] = selectedUploadedFile.fileName;
            payload["attachmentSize"] = selectedUploadedFile.fileSize;
        }

        setLoadingSendMessage(true);
        MessageApi.createOrUpdate<Message, Message>(0, payload as Message)
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    messageSend(response);

                    ((response.messageThread as Entity)?.members as User[])
                        ?.filter(
                            (m) => (m as User)?.meta?.id !== loggedInUserId
                        )
                        ?.forEach((m) => {
                            console.debug(m);
                            userNotificationSend(response);
                        });
                    setMessages((prev) => [response, ...prev]);
                    setMessageText("");
                    if (!isThreadExist) {
                        setMessageThreads((prev) => [
                            response?.messageThread as Entity,
                            ...prev.filter((f) => f.id !== 0),
                        ]);
                        setActiveMessageThread(
                            response?.messageThread as Entity
                        );
                    }
                    if (selectedUploadedFile) {
                        handleCancelAttatch();
                    }
                    localStorage.removeItem(LSC.APP_THREAD_INFO);
                }
            })
            .finally(() => setLoadingSendMessage(false));
    };

    const markReadHandler = async (thread: Entity) => {
        const payload = {
            messageThread: EntityApi.toResourceIRI(thread.id),
        };
        MessageApi.postMarkRead<Message, Message>(payload as Message).then(
            ({ errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                }
            }
        );
    };

    const downloadAttachmentHandler = useCallback((message: Message) => {
        if (message.id > 0) {
            UserApi.postDownload<BlobPart>(
                message.id,
                APPC.BE.Upload.FILETYPE.FILETYPE_MESSAGE_FILE
            ).then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    const fileURL = URL.createObjectURL(new Blob([response]));
                    const alink = document.createElement("a");
                    alink.href = fileURL;
                    alink.download = message?.attachmentName || "";
                    alink.click();
                }
            });
        }
    }, []);

    const fetchMessageThreadRedirected = async () => {
        const threadInfo = JSON.parse(
            localStorage.getItem(LSC.APP_THREAD_INFO) || "null"
        );
        if (!threadInfo) {
            return undefined;
        }
        if (threadInfo.activeTab) {
            setActiveTab(threadInfo.type);
            redirectMessageClearActiveTab();
        }

        const params = {
            itemsPerPage: 1,
            ["order[updatedAt]"]: "desc",
            messageThreadType: threadInfo.type,
            ["xMembers.id"]: Number(threadInfo.targetUser.meta.id),
        };

        return await EntityApi.getCollection<Entity>(1, params, (c) => {
            cancelTokenSourcesRef.current.push(c);
        }).then(({ errorMessage, response }) => {
            let newThread: Entity | undefined = undefined;
            if (errorMessage) {
                toast({
                    title: errorMessage,
                    status: "error",
                });
            } else if (response !== null) {
                if (response.totalItems > 0) {
                    newThread = response.items[0];
                } else {
                    newThread = new Entity({
                        messageThreadType: threadInfo.type,
                        members: [threadInfo.targetUser],
                    });
                    if (threadInfo.companyId) {
                        newThread.company = threadInfo.companyId;
                    } else if (threadInfo.schoolId) {
                        newThread.school = threadInfo.schoolId;
                    }
                    if (
                        threadInfo.type ===
                        APPC.BE.MessageThread.TYPE.TYPE_INTERNSHIP
                    ) {
                        newThread.internshipSchedule =
                            threadInfo.internshipSchedule;
                    }
                }
            }
            return newThread;
        });
    };

    const fetchMessageThreadData = async (
        pageNo: number = 1,
        newThread?: Entity
    ) => {
        setLoadingMessageThread(true);
        const params = {
            itemsPerPage: APPC.PAGE_SIZES[1],
            messageThreadType: activeTab as string,
            [`order[updatedAt]`]: "desc",
        };
        if (search) {
            params["message_thread_search"] = search;
        }

        await EntityApi.getCollection<Entity>(pageNo, params, (c) => {
            cancelTokenSourcesRef.current.push(c);
        })
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setPagesCount(
                        Math.ceil(response.totalItems / APPC.PAGE_SIZES[1])
                    );
                    if (pageNo === 1) {
                        const newItems =
                            newThread &&
                            newThread.messageThreadType === activeTab
                                ? response.items.filter(
                                      (f) => f.id !== newThread.id
                                  )
                                : response.items;
                        if (
                            newThread &&
                            newThread.messageThreadType === activeTab
                        ) {
                            newItems.unshift(newThread);
                        }
                        setMessageThreads(newItems);

                        // Logic to active thread
                        if (newThread) {
                            setActiveMessageThread(newThread);
                        }
                    } else {
                        setMessageThreads((prev) => [
                            ...prev,
                            ...response.items,
                        ]);
                    }
                    setPage(pageNo);
                }
            })
            .finally(() => setLoadingMessageThread(false));
    };

    const fetchMessageData = async (
        messageThreadId: number,
        pageNo: number = 1
    ) => {
        if (messageThreadId < 1) {
            return;
        }
        setLoadingMessage(true);
        const params = {
            itemsPerPage: APPC.PAGE_SIZES[1],
            "messageThread.id": messageThreadId,
            "messageThread.messageThreadType": activeTab as string,
            [`order[createdAt]`]: "desc",
            [`order[id]`]: "desc",
        };

        await MessageApi.getCollection<Message>(pageNo, params, (c) => {
            cancelTokenSourcesRef.current.push(c);
        })
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setMessagePagesCount(
                        Math.ceil(response.totalItems / APPC.PAGE_SIZES[1])
                    );
                    if (pageNo === 1) {
                        setMessages(response.items);
                    } else {
                        setMessages((prev) => [...prev, ...response.items]);
                    }
                    setMessagePage(pageNo);
                }
            })
            .finally(() => setLoadingMessage(false));
    };

    const handleMessageThreadScroll = (
        event: React.UIEvent<HTMLDivElement>
    ) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const isBottom = scrollTop + clientHeight >= scrollHeight - 1;

        if (isBottom && !loadingMessageThread && page < pagesCount) {
            fetchMessageThreadData(page + 1);
        }
    };

    const handleMessageScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const isTop = Math.abs(scrollTop) >= scrollHeight - clientHeight - 1;

        if (
            isTop &&
            !loadingMessage &&
            messagePage < messagePagesCount &&
            activeMessageThread
        ) {
            fetchMessageData(activeMessageThread.id, messagePage + 1);
        }
    };

    const fetchData = async () => {
        const newThread = await fetchMessageThreadRedirected();
        if (activeTab) {
            if (messageThreadListRef.current) {
                messageThreadListRef.current.scrollTop = 0;
            }
            await fetchMessageThreadData(1, newThread);
        }
    };

    // useEffect
    useEffect(() => {
        socket.on(SOCKET_EVENT.ON.MESSAGE_RECEIVE, (msg: Message) => {
            if ((msg.user as User)?.meta?.id !== loggedInUserId) {
                setMessages((prev) => [msg, ...prev]);
                markReadHandler(msg.messageThread as Entity);
            }
        });

        socket.on(SOCKET_EVENT.ON.USER_NOTIFICATION_RECEIVE, (msg: Message) => {
            if (
                activeMessageThread?.id !== (msg?.messageThread as Entity)?.id
            ) {
                setMessageThreads((prev) => [
                    msg?.messageThread as Entity,
                    ...prev.filter(
                        (f) => f.id !== (msg?.messageThread as Entity)?.id
                    ),
                ]);
            }
        });

        return () => {
            socket.off(SOCKET_EVENT.ON.MESSAGE_RECEIVE);
            socket.off(SOCKET_EVENT.ON.USER_NOTIFICATION_RECEIVE);
        };
    }, []);

    useEffect(() => {
        if (!activeTab) {
            setActiveTab(APPC.BE.MessageThread.TYPE.TYPE_O2O);
        }
        setActiveMessageThread(undefined);
        fetchData();
    }, [activeTab]);

    useEffect(() => {
        fetchData();
    }, [search]);

    useEffect(() => {
        setMessages([]);
        setMessagePage(1);
        setMessagePagesCount(0);
        if (activeMessageThread) {
            setSelectedMessageThread(activeMessageThread.id);
            messageThreadJoin(`thread:${activeMessageThread.id}`);
            fetchMessageData(activeMessageThread.id, 1);

            if (activeMessageThread.id > 0) {
                markReadHandler(activeMessageThread);
                setNewThreadMessages(
                    newThreadMessages.filter(
                        (num) => num !== activeMessageThread.id
                    )
                );
            }

            const member = Array.isArray(activeMessageThread?.members)
                ? (activeMessageThread.members as User[]).find((member) =>
                      activeMessageThread.messageThreadType ===
                      APPC.BE.MessageThread.TYPE.TYPE_O2O
                          ? member?.meta?.id !== loggedInUserId
                          : member?.meta?.userType ===
                            APPC.BE.User.USERTYPE.USERTYPE_STUDENT
                  )
                : undefined;
            setActiveMessageThreadUser(member);
            messageInputRef.current?.focus();

            return () => {
                messageThreadLeave(`thread:${activeMessageThread.id}`);
            };
        } else {
            setActiveMessageThreadUser(undefined);
            setSelectedMessageThread(undefined);
        }
    }, [activeMessageThread]);

    return (
        <>
            <Flex flex="1" overflow="auto">
                {(!isMobile || (isMobile && !activeMessageThread)) && (
                    <VStack
                        alignItems={"flex-start"}
                        gap={0}
                        borderRight={
                            "1px solid var(--chakra-colors-primaryT80)"
                        }
                        w={isMobile ? "100%" : "auto"}
                    >
                        <VStack
                            minW={{ base: "auto", sm: "auto", md: "350px" }}
                            minH={"4.875rem"}
                            px={{ base: 3, sm: 6 }}
                            py={{ base: 3.5, sm: 4, md: "1.125rem" }}
                            gap={4}
                            justifyContent={"center"}
                            borderBottom={
                                "1px solid var(--chakra-colors-primaryT80)"
                            }
                            w={isMobile ? "100%" : "auto"}
                            position={"sticky"}
                        >
                            <AppSearch
                                onSearch={(value) => {
                                    setSearch(value);
                                    if (page > 1) {
                                        setPage(1);
                                    }
                                }}
                            />
                        </VStack>
                        <HStack
                            minW={{ base: "auto", sm: "auto", md: "350px" }}
                            px={{ base: 3, sm: 6 }}
                            pt={{ base: 3.5, sm: 4, md: "1.125rem" }}
                            justifyContent={"space-between"}
                            w={isMobile ? "100%" : "auto"}
                            gap={0}
                        >
                            <Button
                                w={"full"}
                                size={"sm"}
                                variant="tab"
                                isActive={
                                    activeTab ===
                                    APPC.BE.MessageThread.TYPE.TYPE_O2O
                                }
                                onClick={() =>
                                    setActiveTab(
                                        APPC.BE.MessageThread.TYPE.TYPE_O2O
                                    )
                                }
                            >
                                <AppIcon name="icl-id-badge" w="1rem" />
                                <Hide below="sm">
                                    <Text ml={2}>
                                        {t("con.MessageThread:TYPE_O2O")}
                                    </Text>
                                </Hide>
                            </Button>
                            <Button
                                w={"full"}
                                size={"sm"}
                                variant="tab"
                                isActive={
                                    activeTab ===
                                    APPC.BE.MessageThread.TYPE.TYPE_INTERNSHIP
                                }
                                onClick={() =>
                                    setActiveTab(
                                        APPC.BE.MessageThread.TYPE
                                            .TYPE_INTERNSHIP
                                    )
                                }
                            >
                                <AppIcon name="icl-book" w="1rem" />
                                <Hide below="sm">
                                    <Text ml={2}>
                                        {t("con.MessageThread:TYPE_INTERNSHIP")}
                                    </Text>
                                </Hide>
                            </Button>
                            {/* <Button
                                w={"full"}
                                size={"sm"}
                                variant="tab"
                                isActive={
                                    activeTab ===
                                    APPC.BE.MessageThread.TYPE.TYPE_GROUP
                                }
                                onClick={() =>
                                    setActiveTab(
                                        APPC.BE.MessageThread.TYPE.TYPE_GROUP
                                    )
                                }
                            >
                                <AppIcon name="icl-group" w="1rem" />
                                <Hide below="sm">
                                    <Text ml={2}>
                                        {t("con.MessageThread:TYPE_GROUP")}
                                    </Text>
                                </Hide>
                            </Button> */}
                        </HStack>
                        <VStack
                            ref={messageThreadListRef}
                            alignItems={"flex-start"}
                            minW={{ base: "auto", sm: "auto", md: "350px" }}
                            maxH="calc(100vh - 150px)"
                            overflowY="auto"
                            p={{ base: 3, sm: 6 }}
                            gap={2}
                            w={isMobile ? "100%" : "auto"}
                            sx={{ scrollbarWidth: "thin" }}
                            onScroll={handleMessageThreadScroll}
                        >
                            {messageThreads.map((thread) => {
                                return (
                                    <AppMessageThread
                                        key={thread.id}
                                        data={thread as Entity}
                                        loggedInUser={user as User}
                                        activeMessageThreadId={
                                            activeMessageThread?.id
                                        }
                                        onClickHandler={(
                                            messageThread: Entity
                                        ) => {
                                            setActiveMessageThread(
                                                messageThread
                                            );
                                        }}
                                        isNew={newThreadMessages.includes(
                                            thread.id
                                        )}
                                    />
                                );
                            })}
                            {loadingMessageThread && (
                                <Flex
                                    flex={1}
                                    alignSelf={"center"}
                                    justifyContent={"center"}
                                >
                                    <Spinner
                                        size="lg"
                                        thickness="4px"
                                        speed="0.65s"
                                        color="var(--chakra-colors-primary)"
                                        emptyColor="var(--chakra-colors-greyT90)"
                                    />
                                </Flex>
                            )}
                        </VStack>
                    </VStack>
                )}
                {(!isMobile || (isMobile && activeMessageThread)) && (
                    <VStack
                        w={"full"}
                        h={"full"}
                        alignItems={"stretch"}
                        gap={0}
                    >
                        <HStack
                            w={"full"}
                            px={6}
                            borderBottom={
                                "1px solid var(--chakra-colors-primaryT80)"
                            }
                            justifyContent={"flex-end"}
                            flexWrap={"wrap"}
                            gap={0}
                            py={"1.125rem"}
                            position={"sticky"}
                        >
                            <Box
                                display={"flex"}
                                alignItems={"center"}
                                flexGrow={1}
                                mr={6}
                                h={"41px"}
                                flexWrap={"wrap"}
                            >
                                <Text
                                    textStyle={{
                                        base: "heading5",
                                        sm: "heading4",
                                    }}
                                    cursor="pointer"
                                    onClick={() => {
                                        setActiveMessageThread(undefined);
                                    }}
                                >
                                    {t("padm.MessagesPage:text.pageTitle")}
                                </Text>
                                {activeMessageThreadUser && (
                                    <Text
                                        textStyle={{
                                            base: "heading5",
                                            sm: "heading4",
                                        }}
                                        noOfLines={1}
                                    >
                                        <Box
                                            as="span"
                                            fontSize="1.8rem"
                                            transform="scale(.6, 1)"
                                            color="var(--chakra-colors-primaryT50)"
                                            mx={3}
                                        >
                                            &gt;
                                        </Box>
                                        {grant.isStudent &&
                                        activeMessageThread?.messageThreadType ===
                                            APPC.BE.MessageThread.TYPE
                                                .TYPE_INTERNSHIP
                                            ? (
                                                  activeMessageThread?.internshipSchedule as InternshipSchedule
                                              )?.name
                                            : activeMessageThreadUser?.meta
                                                  ?.fullName}
                                    </Text>
                                )}
                            </Box>
                            {/* <Box alignSelf={"center"}>
                                <Button variant={"unstyled"}>
                                    <AppIcon name="icl-options" />
                                </Button>
                            </Box> */}
                        </HStack>
                        <VStack
                            overflowY="auto"
                            alignItems={"stretch"}
                            borderBottom={
                                "1px solid var(--chakra-colors-primaryT80)"
                            }
                            gap={1}
                            p={6}
                            flexGrow={1}
                            onScroll={handleMessageScroll}
                            flexDirection={
                                activeMessageThread
                                    ? "column-reverse"
                                    : "column"
                            }
                        >
                            {activeMessageThread &&
                                Object.entries(groupedMessages).map(
                                    ([date, msgs], index) => (
                                        <Fragment key={`group-${index}`}>
                                            {(msgs as Message[]).map((msg) => (
                                                <Fragment key={`msg-${msg.id}`}>
                                                    <AppMessage
                                                        data={msg}
                                                        loggedInUserId={
                                                            loggedInUserId
                                                        }
                                                        onDownload={
                                                            downloadAttachmentHandler
                                                        }
                                                    />
                                                </Fragment>
                                            ))}
                                            <Box position={"relative"} my={2}>
                                                <Divider
                                                    color={
                                                        "var(--chakra-colors-primaryT80)"
                                                    }
                                                />
                                                <AbsoluteCenter
                                                    bg={"white"}
                                                    px={4}
                                                >
                                                    <Badge
                                                        variant={"green"}
                                                        size={"sm"}
                                                    >
                                                        {date}
                                                    </Badge>
                                                </AbsoluteCenter>
                                            </Box>
                                        </Fragment>
                                    )
                                )}
                            {!activeMessageThread && (
                                <VStack flexGrow={1} justifyContent={"center"}>
                                    <Box>
                                        <AppIcon
                                            name="icl-chat-bubble"
                                            w="4rem"
                                            color="var(--chakra-colors-greyT80)"
                                        />
                                    </Box>
                                    <Text
                                        textStyle={"regularSm"}
                                        color="var(--chakra-colors-contentT60)"
                                    >
                                        {t(
                                            "padm.MessagesPage:emptyState.message"
                                        )}
                                    </Text>
                                </VStack>
                            )}
                            {loadingMessage && (
                                <Flex flex={1} justifyContent={"center"} pt={8}>
                                    <Spinner
                                        size="lg"
                                        thickness="4px"
                                        speed="0.65s"
                                        color="var(--chakra-colors-primary)"
                                        emptyColor="var(--chakra-colors-greyT90)"
                                    />
                                </Flex>
                            )}
                        </VStack>
                        {activeMessageThread && (
                            <Flex
                                flexDir={"column"}
                                bg="var(--chakra-colors-primaryT90)"
                                justifyContent={"flex-start"}
                            >
                                <Textarea
                                    ref={messageInputRef}
                                    name="sendMessage"
                                    px={6}
                                    placeholder={t(
                                        "padm.MessagesPage:sendMessage.placeholder"
                                    )}
                                    resize="none"
                                    h="80px"
                                    border={"none"}
                                    borderRadius={"none"}
                                    _focus={{ boxShadow: "none" }}
                                    bg="transparent"
                                    value={messageText}
                                    onChange={(e) =>
                                        setMessageText(e.currentTarget.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.ctrlKey && e.key === "Enter") {
                                            e.preventDefault();
                                            submitMessageHandler();
                                        }
                                    }}
                                />
                                <HStack
                                    h={"full"}
                                    px={6}
                                    py={2}
                                    justifyContent={"space-between"}
                                    bg="var(--chakra-colors-primaryT80)"
                                    gap={4}
                                    wrap={isMobile ? "wrap" : "nowrap"}
                                >
                                    <HStack>
                                        <input
                                            type="file"
                                            hidden
                                            ref={fileInputRef}
                                            onChange={handleFileSelect}
                                        />
                                        <Button
                                            size={"sm"}
                                            variant={"unstyled"}
                                            onClick={() => {
                                                fileInputRef.current?.click();
                                            }}
                                        >
                                            <AppIcon
                                                name={"icl-paperclip"}
                                                w="1.25rem"
                                                color={
                                                    "var(--chakra-colors-primary)"
                                                }
                                            />
                                        </Button>
                                        {selectedFile && (
                                            <>
                                                <Center height="1.75rem">
                                                    <Divider
                                                        orientation="vertical"
                                                        borderColor={
                                                            "var(--chakra-colors-primary)"
                                                        }
                                                    />
                                                </Center>
                                                <HStack>
                                                    <Box>
                                                        <Text
                                                            textStyle={
                                                                "regularSm"
                                                            }
                                                            lineHeight={1.1}
                                                            maxW={
                                                                isMobile
                                                                    ? "175px"
                                                                    : "auto"
                                                            }
                                                            noOfLines={1}
                                                            color={
                                                                backendError
                                                                    ? "var(--chakra-colors-destructive)"
                                                                    : ""
                                                            }
                                                        >
                                                            {backendError
                                                                ? backendError
                                                                : selectedUploadedFile
                                                                  ? selectedUploadedFile.fileName
                                                                  : selectedFile.name}
                                                        </Text>
                                                        {uploadProgress > 0 &&
                                                            uploadProgress <=
                                                                100 && (
                                                                <Progress
                                                                    colorScheme="green"
                                                                    height="3px"
                                                                    value={
                                                                        uploadProgress
                                                                    }
                                                                    mt={1.5}
                                                                />
                                                            )}
                                                    </Box>
                                                    <Button
                                                        size={"sm"}
                                                        variant={"unstyled"}
                                                        onClick={
                                                            handleCancelAttatch
                                                        }
                                                    >
                                                        <AppIcon
                                                            name={"icl-x"}
                                                            w="1rem"
                                                            color={
                                                                "var(--chakra-colors-primary)"
                                                            }
                                                        />
                                                    </Button>
                                                </HStack>
                                            </>
                                        )}
                                    </HStack>
                                    <HStack gap={3}>
                                        <AppEmojiPicker
                                            emojiState={emojiState}
                                            onEmojiClick={(e) =>
                                                setMessageText(
                                                    (prev) => prev + e.emoji
                                                )
                                            }
                                        />
                                        <Button
                                            size="xs"
                                            variant={"primary"}
                                            className="btn-icon-xs"
                                            onClick={emojiState.onToggle}
                                        >
                                            <AppIcon
                                                name="icl-happy"
                                                w={"0.9rem"}
                                            />
                                        </Button>
                                        <Button
                                            size="xs"
                                            variant={"primary"}
                                            onClick={submitMessageHandler}
                                            disabled={
                                                loadingSendMessage ||
                                                loadingAttatch
                                            }
                                        >
                                            <AppIcon
                                                name="ics-paper-plane"
                                                w="0.75rem"
                                            />
                                            <Text ml={2}>
                                                {t(
                                                    "padm.MessagesPage:button.send"
                                                )}
                                            </Text>
                                        </Button>
                                    </HStack>
                                </HStack>
                            </Flex>
                        )}
                    </VStack>
                )}
            </Flex>
        </>
    );
};
